import axios from "axios";
import API_BASE_URL, { WS_BASE_URL } from "./config";
import { logoutUser } from "./auth";

const AUTH_BOOTSTRAP_STORAGE_KEY = "valpha:auth-bootstrap-until";
const AUTH_BOOTSTRAP_WINDOW_MS = 15000;

let isRefreshing = false;
let refreshQueue = [];

// Session gating: every login increments the counter and stamps requests.
// 401 handlers from stale sessions are silently rejected instead of
// cascading logoutUser() calls that wipe the new session's token.
let sessionCounter = 0;
let activeSessionId = 0;
if (typeof window !== "undefined" && localStorage.getItem("token")) {
  // Page refresh with an existing token — treat as an active session so
  // stale 401 handlers after a later logout can't cascade.
  sessionCounter = 1;
  activeSessionId = 1;
}

// AbortController tied to the current session.  When a new session starts
// (login) or the current one ends (logout), all in-flight requests from
// the old session are cancelled so their 401 handlers can never fire.
let sessionAbortController = new AbortController();

const getCurrentToken = () => localStorage.getItem("token");
const getRefreshToken = () => localStorage.getItem("refresh_token");

const markAuthBootstrapWindow = () => {
  try {
    sessionStorage.setItem(
      AUTH_BOOTSTRAP_STORAGE_KEY,
      String(Date.now() + AUTH_BOOTSTRAP_WINDOW_MS),
    );
  } catch {
    // Storage access can fail in locked-down browser contexts.
  }
};

const clearAuthBootstrapWindow = () => {
  try {
    sessionStorage.removeItem(AUTH_BOOTSTRAP_STORAGE_KEY);
  } catch {
    // Storage access can fail in locked-down browser contexts.
  }
};

const isWithinAuthBootstrapWindow = () => {
  try {
    const raw = sessionStorage.getItem(AUTH_BOOTSTRAP_STORAGE_KEY);
    if (!raw) return false;

    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      sessionStorage.removeItem(AUTH_BOOTSTRAP_STORAGE_KEY);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const isAuthEndpoint = (url = "") => /\/token(?:\/refresh)?\/?$/.test(url);

const getRequestMethod = (config) => String(config?.method || "get").toLowerCase();

const shouldHoldFreshLoginSession = (config) => (
  isWithinAuthBootstrapWindow() && getRequestMethod(config) === "get"
);

const buildApiError = (error, fallbackMessage = "An unexpected error occurred. Please try again.") => {
  let userFriendlyMessage = fallbackMessage;

  if (
    error.response &&
    error.response.data &&
    error.response.data.detail &&
    Array.isArray(error.response.data.detail) &&
    error.response.data.detail.length > 0 &&
    error.response.data.detail[0].msg
  ) {
    userFriendlyMessage = error.response.data.detail[0].msg;
  } else if (error.response && error.response.data && error.response.data.detail) {
    userFriendlyMessage = error.response.data.detail;
  } else if (error.response && error.response.data && typeof error.response.data === "object") {
    const fieldErrors = Object.entries(error.response.data)
      .map(([, msgs]) => (Array.isArray(msgs) ? msgs.join(", ") : msgs))
      .join(". ");
    if (fieldErrors) userFriendlyMessage = fieldErrors;
  } else if (error.message) {
    userFriendlyMessage = error.message;
  }

  const enriched = new Error(userFriendlyMessage);
  if (error.response) {
    enriched.status = error.response.status;
    enriched.data = error.response.data;
    enriched.headers = error.response.headers;
  }
  enriched.original = error;
  return enriched;
};

const retryRequestWithToken = (config, token) => api({
  ...config,
  headers: {
    ...(config?.headers || {}),
    Authorization: `Bearer ${token}`,
  },
  _retry: true,
});

const enqueueRefreshRetry = (config) => new Promise((resolve, reject) => {
  refreshQueue.push({ config, resolve, reject });
});

const flushRefreshQueue = (token, error = null) => {
  refreshQueue.forEach(({ config, resolve, reject }) => {
    if (token) {
      resolve(retryRequestWithToken(config, token));
      return;
    }
    reject(error || new Error("Unauthorized"));
  });
  refreshQueue = [];
};

const attemptTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }

  const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
    refresh: refreshToken,
  });
  const nextAccessToken = response?.data?.access;
  if (!nextAccessToken) {
    throw new Error("Refresh response did not include an access token");
  }

  // Guard: if the refresh token changed while the network call was in-flight,
  // another login/logout happened — discard this result to avoid overwriting
  // the new session's token.
  if (getRefreshToken() !== refreshToken) {
    throw new Error("Session changed during token refresh");
  }

  localStorage.setItem("token", nextAccessToken);
  // NOTE: emitAuthLogin() is intentionally NOT called during token refresh.
  // The interceptor updates localStorage silently so subsequent requests use
  // the refreshed token.  Calling emitAuthLogin() here would trigger a React
  // state update that can cause premature route navigation during login flow.
  return nextAccessToken;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const emitAuthLogin = () => {
  try {
    window.dispatchEvent(new Event("valpha:login"));
  } catch {
    // Ignore event dispatch failures in restricted contexts.
  }
};

export const emitProfileUpdated = () => {
  try {
    window.dispatchEvent(new Event("valpha:profile-updated"));
  } catch {
    // Ignore event dispatch failures in restricted contexts.
  }
};

// Reset in-flight refresh state on logout so a stale refresh from the
// previous session cannot overwrite the next session's token.
if (typeof window !== "undefined") {
  window.addEventListener("valpha:logout", () => {
    activeSessionId = 0;
    isRefreshing = false;
    flushRefreshQueue(null, new Error("Logged out"));
    // Cancel in-flight requests so stale 401 handlers can never fire.
    sessionAbortController.abort();
    sessionAbortController = new AbortController();
  });
}

export const waitForAuthenticatedSession = async (delays = [0, 250, 750, 1500, 2500]) => {
  let lastError = null;

  for (const waitMs of delays) {
    if (waitMs > 0) {
      await delay(waitMs);
    }

    try {
      await api.get("/accounts/profile/");
      return true;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Authenticated session did not stabilize.");
};

// --- Centralized Error Handling ---
//
// Design principle: logoutUser() is ONLY called when the refresh token
// itself is rejected by the server (401/403 on /token/refresh/).  That is
// the single definitive signal that the session is dead.
//
// A 401 on any regular endpoint (profile, bots, trades, …) is NEVER a
// reason to destroy the session.  It just means "try refreshing the access
// token".  If the refresh succeeds, retry the request.  If the retry still
// 401s, reject the promise — the caller can handle it.  No logout.
//
// Retry pipeline for a 401:
//   1. Bootstrap retry  — if within the 15 s login window, retry once with
//      the current token after a short delay (transient propagation fix).
//   2. Token refresh    — exchange the refresh token for a fresh access
//      token and retry the request.
//   3. Reject           — if both fail, reject the promise for the caller.
//
// _authRetryHandled prevents re-entry: retries go through the interceptor
// and call handleApiError again; the flag (spread from the original config)
// ensures we reject immediately on re-entry instead of looping.
const handleApiError = async (error) => {
  // ── 402 Payment Required → broadcast for global Upgrade modal ──
  // The subscriptions decorator denies with 402 + {feature, reason, ...}.
  // We emit a window event so any UI shell can react (e.g. show a modal).
  if (error?.response && error.response.status === 402) {
    try {
      const payload = error.response.data || {};
      window.dispatchEvent(new CustomEvent("valpha:feature-gate", { detail: payload }));
    } catch { /* non-browser environments */ }
  }

  if (error.response && error.response.status === 401) {
    const originalConfig = error.config || {};

    // Session gate: stale session → silently reject.
    if (originalConfig._sessionId !== activeSessionId) {
      return Promise.reject(buildApiError(error, "Unauthorized"));
    }

    // Re-entry guard: retried requests (bootstrap / refresh) that 401
    // again are rejected immediately — no infinite loops.
    if (originalConfig._authRetryHandled) {
      return Promise.reject(buildApiError(error, "Unauthorized"));
    }
    originalConfig._authRetryHandled = true;

    const requestUrl = originalConfig.url || "";

    // ── Step 1: Bootstrap retry ──
    // During the bootstrap window the token is fresh — a 401 is most
    // likely a transient multi-server propagation issue.  Retry once with
    // the current token after a short delay.
    // Skip if a token refresh is already running (no point retrying with
    // the same token another request already proved invalid).
    if (shouldHoldFreshLoginSession(originalConfig) && !isRefreshing) {
      await delay(300);
      try {
        return await retryRequestWithToken(originalConfig, getCurrentToken());
      } catch (bootstrapError) {
        // Only fall through to token refresh on 401.  Other errors (500,
        // network, abort) are returned as-is.
        if (bootstrapError?.status !== 401) {
          return Promise.reject(bootstrapError);
        }
        // 401 on bootstrap retry → continue to token refresh below.
      }
    }

    // ── Step 2: Token refresh ──
    const refreshToken = getRefreshToken();
    if (!refreshToken || isAuthEndpoint(requestUrl)) {
      return Promise.reject(buildApiError(error, "Unauthorized"));
    }

    // Another request already started refreshing → queue behind it.
    if (isRefreshing) {
      return enqueueRefreshRetry(originalConfig);
    }

    isRefreshing = true;

    try {
      const nextAccessToken = await attemptTokenRefresh();
      flushRefreshQueue(nextAccessToken);
      return retryRequestWithToken(originalConfig, nextAccessToken);
    } catch (refreshError) {
      flushRefreshQueue(null, refreshError);

      // The refresh token was rejected → session is truly dead.
      // Only logout if this is still the active session (a new login
      // hasn't started while we were waiting for the refresh).
      const refreshStatus = refreshError?.response?.status;
      const isRefreshRejected = refreshStatus === 401 || refreshStatus === 403;

      if (isRefreshRejected && originalConfig._sessionId === activeSessionId) {
        clearAuthBootstrapWindow();
        logoutUser();
        window.location.href = "/login";
      }

      return Promise.reject(buildApiError(refreshError, "Unauthorized"));
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(buildApiError(error));
};

// --- Axios Instances ---

// Main API instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

api.interceptors.request.use((config) => {
  config._sessionId = activeSessionId;
  // Link to session AbortController so login/logout cancels stale requests.
  if (!config.signal) {
    config.signal = sessionAbortController.signal;
  }
  const token = localStorage.getItem("token");
  if (token) {
    if (!config.headers) config.headers = {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  handleApiError
);

// MT5 calls go through the same API gateway but at /mt5/ (not /api/mt5/),
// so we use a separate instance without the /api prefix.
const mt5Api = axios.create({
  baseURL: API_BASE_URL,
});

mt5Api.interceptors.request.use((config) => {
  config._sessionId = activeSessionId;
  if (!config.signal) {
    config.signal = sessionAbortController.signal;
  }
  const token = localStorage.getItem("token");
  if (token) {
    if (!config.headers) config.headers = {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

mt5Api.interceptors.response.use(
  (response) => response.data,
  handleApiError
);


// **1️⃣ User Authentication**
export const loginUser = async (identifier, password) => {
  try {
    // Cancel all in-flight requests from the previous session.  Their 401
    // handlers can never run, eliminating the entire class of stale-session
    // race conditions (token overwrites, spurious logoutUser calls, etc.).
    sessionAbortController.abort();
    sessionAbortController = new AbortController();

    const response = await axios.post(`${API_BASE_URL}/api/token/`, {
      email: identifier,
      password,
    });
    const responseData = response.data;

    if (responseData && responseData.access) {
      localStorage.setItem("token", responseData.access);
    }
    if (responseData && responseData.refresh) {
      localStorage.setItem("refresh_token", responseData.refresh);
    }
    markAuthBootstrapWindow();
    activeSessionId = ++sessionCounter;
    // NOTE: emitAuthLogin() is intentionally NOT called here.
    // The Login component calls it after session verification to prevent
    // a premature re-render that would navigate to /dashboard before
    // waitForAuthenticatedSession() completes.
    return responseData;
  } catch (error) {
    throw buildApiError(error, "Login failed");
  }
};

export const exchangeAdminImpersonation = async (rawToken) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/api/accounts/admin-impersonation/exchange/`,
      { token: rawToken },
    );
    const responseData = response.data;
    if (!responseData?.access || !responseData?.refresh) {
      throw new Error("Login response did not include session tokens.");
    }

    // This flow may be opened in a browser profile with another Valpha user
    // already signed in. Tear that session down before installing the target
    // user's tokens so no user-scoped state survives the switch.
    logoutUser();
    sessionAbortController = new AbortController();
    localStorage.setItem("token", responseData.access);
    localStorage.setItem("refresh_token", responseData.refresh);
    markAuthBootstrapWindow();
    activeSessionId = ++sessionCounter;
    return responseData;
  } catch (error) {
    throw buildApiError(error, "The admin login link is invalid or has expired.");
  }
};

export const registerUser = (userData) => api.post("/accounts/register/", userData);

export const verifyEmail = (token) => api.post("/accounts/verify-email/", { token });

export const resendVerification = (email) => api.post("/accounts/resend-verification/", { email });

export const requestPasswordReset = (email) =>
  api.post("/accounts/password-reset/", { email });

export const confirmPasswordReset = (resetData) =>
  api.post("/accounts/password-reset/confirm/", resetData);

// **2️⃣ Fetch User Accounts**
export const fetchAccounts = (options = {}) => api.get("/accounts/", options);
export const createAccount = (accountData) => api.post("/accounts/create/", accountData);
export const fetchAccountDetails = (accountId, options = {}) => api.get(`/accounts/details/${accountId}/`, options);

// **3️⃣ Onboarding**
export const startOnboarding = (onboardData) => mt5Api.post("/mt5/onboard", onboardData);
export const getOnboardStatus = (accountId) => mt5Api.get(`/mt5/onboard/${accountId}/status`);
export const completeOnboarding = (accountId) => mt5Api.post(`/mt5/onboard/${accountId}/complete`);

/**
 * Start onboarding and poll until VNC is ready (handles async provisioning).
 * Returns the final response with vnc_details.
 * @param {object} onboardData - {internal_account_id: string}
 * @param {number} pollInterval - ms between status polls (default 3000)
 * @param {number} maxWait - max ms to wait before giving up (default 600000 = 10 min)
 */
export const startOnboardingAndWait = async (onboardData, pollInterval = 3000, maxWait = 600000) => {
  const res = await startOnboarding(onboardData);
  if (res.status === "ready" && res.vnc_details) return res;

  // Async provisioning — poll until ready
  const accountId = onboardData.internal_account_id;
  const deadline = Date.now() + maxWait;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollInterval));
    const status = await getOnboardStatus(accountId);
    if (status.status === "ready" && status.vnc_details) return status;
    if (status.status === "failed") throw new Error(status.error || "Onboarding failed");
  }
  throw new Error("Onboarding timed out — provisioning took too long");
};

// **4️⃣ Delete an Account**
export const deleteAccount = (accountId) => api.delete(`/accounts/delete/${accountId}/`);

// **5️⃣ Trade Execution**
export const executeTrade = (tradeData) => api.post("/trades/execute/", tradeData);
export const fetchOpenTrades = (accountId, options = {}) => api.get(`/trades/open-positions/${accountId}/`, options);
export const closeTrade = (tradeId, data) => api.delete(`/trades/close/${tradeId}/`, { params: data });
export const closeOrphanPosition = (payload) => api.post(`/trades/close-orphan/`, payload);
export const updateStopLossApi = (payload) => api.post("/trades/update-sl/", payload);
export const updateTakeProfitApi = (tradeUuid, takeProfit) => api.put(`/trades/update-tp/${tradeUuid}/`, { take_profit: takeProfit });
export const partialCloseTradeApi = (tradeUuid, volumeToClose) => api.post(`/trades/${tradeUuid}/partial-close/`, { volume_to_close: volumeToClose });
export const cancelPendingOrder = (orderId) => api.delete(`/trades/pending-orders/${orderId}/cancel/`);

// **5b️⃣ Trade Approvals**
export const listPendingApprovals = (status = 'PENDING') => api.get(`/trades/pending/`, { params: { status } });
export const getPendingApproval = (id) => api.get(`/trades/pending/${id}/`);
export const getPendingApprovalCount = (options = {}) => api.get(`/trades/pending/count/`, options);
export const approveTrade = (id, overrides = {}) => api.post(`/trades/pending/${id}/approve/`, overrides);
export const rejectTrade = (id, reason = '') => api.post(`/trades/pending/${id}/reject/`, { reason });

// Manual sync of an account's trades (queues reconciliation on backend)
export const syncAccountTrades = (accountUuid) => api.post(`/trades/accounts/${encodeURIComponent(accountUuid)}/sync/`);

// Copy trading
export const listCopySourceProfiles = (params) => api.get("/copy-trading/source-profiles/", params ? { params } : undefined);
export const discoverCopySourceProfiles = (params) => api.get("/copy-trading/source-profiles/discover/", params ? { params } : undefined);
export const getCopyTradingSummary = () => api.get("/copy-trading/source-profiles/summary/");
export const createCopySourceProfile = (payload) => api.post("/copy-trading/source-profiles/", payload);
export const updateCopySourceProfile = (id, payload) => api.patch(`/copy-trading/source-profiles/${encodeURIComponent(id)}/`, payload);
export const deleteCopySourceProfile = (id) => api.delete(`/copy-trading/source-profiles/${encodeURIComponent(id)}/`);
export const activateCopySourceProfile = (id) => api.post(`/copy-trading/source-profiles/${encodeURIComponent(id)}/activate/`);
export const pauseCopySourceProfile = (id) => api.post(`/copy-trading/source-profiles/${encodeURIComponent(id)}/pause/`);
export const listCopyTradingLinks = (params) => api.get("/copy-trading/links/", params ? { params } : undefined);
export const createCopyTradingLink = (payload) => api.post("/copy-trading/links/", payload);
export const updateCopyTradingLink = (id, payload) => api.patch(`/copy-trading/links/${encodeURIComponent(id)}/`, payload);
export const deleteCopyTradingLink = (id) => api.delete(`/copy-trading/links/${encodeURIComponent(id)}/`);
export const resumeCopyTradingLink = (id) => api.post(`/copy-trading/links/${encodeURIComponent(id)}/resume/`);
export const pauseCopyTradingLink = (id) => api.post(`/copy-trading/links/${encodeURIComponent(id)}/pause/`);
export const listCopySignals = (params) => api.get("/copy-trading/signals/", params ? { params } : undefined);
export const listCopyMirrors = (params) => api.get("/copy-trading/mirrors/", params ? { params } : undefined);
export const listCopyAuditLogs = (params) => api.get("/copy-trading/audit-logs/", params ? { params } : undefined);

// **6️⃣ Watchlist Management** (correct path: /api/trades/watchlists/)
// Optionally pass params (e.g., { account_id }) to filter
export const listWatchlistItems = (params, options = {}) => {
  const config = { ...options };
  if (params) {
    config.params = params;
  }
  return api.get("/trades/watchlists/", Object.keys(config).length ? config : undefined);
};
export const createWatchlistItem = (itemData) => api.post("/trades/watchlists/", itemData, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
export const retrieveWatchlistItem = (itemId) => api.get(`/trades/watchlists/${itemId}/`);
export const updateWatchlistItem = (itemId, itemData) => api.put(`/trades/watchlists/${itemId}/`, itemData);
export const partialUpdateWatchlistItem = (itemId, itemData) => api.patch(`/trades/watchlists/${itemId}/`, itemData, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
export const deleteWatchlistItem = (itemId) => api.delete(`/trades/watchlists/${itemId}/`);
// Convenience: replace account links for an item
export const updateWatchlistLinks = (itemId, accountIds) => {
  const cleaned = Array.isArray(accountIds) ? accountIds.filter((x) => x != null && x !== '') : [];
  return api.patch(
    `/trades/watchlists/${itemId}/`,
    { account_ids: cleaned },
    { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }
  );
};
export const makeItemGlobal = (itemId) => api.patch(`/trades/watchlists/${itemId}/`, { is_global: true }, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
export const removeItemGlobalStatus = (itemId) => api.patch(`/trades/watchlists/${itemId}/`, { is_global: false }, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });

// **7️⃣ MT5 Integration**
export const connectMT5 = (accountId, accountData) => api.post(`/accounts/${accountId}/mt5/connect/`, accountData);
export const connectIB = (accountId) => api.post(`/accounts/${accountId}/ib/connect/`);
export const getMT5Account = (accountId) => api.get(`/accounts/${accountId}/mt5/`);
export const startMT5Vnc = (accountId) => api.post(`/accounts/${accountId}/mt5/vnc/`);

// **7️⃣.1️⃣ Instance Control**
export const shutdownInstance = (instanceId) => api.post(`/control/instance/${instanceId}/shutdown/`);
export const restartInstance = (instanceId) => api.post(`/control/instance/${instanceId}/restart/`);

// **8️⃣ Live Prices & Symbol Info**
export const subscribeToLivePrices = (accountId, symbol, onPriceUpdate) => {
  // Prefer configured WS base origin when available
  const origin = WS_BASE_URL || (() => {
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    return `${protocol}://${window.location.host}`;
  })();
  const ws = new WebSocket(`${origin}/ws/prices/${accountId}/${symbol}`);

  ws.onopen = () => console.log(`🔗 WebSocket connected: Account ${accountId} - ${symbol}`);
  ws.onmessage = (event) => {
    try {
      // Some backends may send pings/keepalives or non-JSON frames during reconnects
      const data = typeof event.data === 'string' ? event.data : '';
      if (!data) return;
      const parsed = JSON.parse(data);
      onPriceUpdate(parsed);
    } catch (e) {
      // Ignore malformed frames to avoid crashing the UI during resizes/reconnects
      console.warn(`LivePrices message parse failed for ${symbol}:`, e);
    }
  };
  ws.onerror = (error) => console.error("❌ WebSocket error:", error);
  ws.onclose = (event) => {
    if (event.code === 4000) {
      console.log(`[LivePrices] Server redeploying, reconnecting ${symbol}...`);
      setTimeout(() => subscribeToLivePrices(accountId, symbol, onPriceUpdate), 100);
      return;
    }
    console.log(`❌ WebSocket closed for ${symbol}`);
  };

  return ws;
};
export const getSymbolInfo = (accountId, symbol) => api.get(`/trades/symbol-info/${accountId}/${symbol}`);
export const fetchATR = (symbol, timeframe) => api.get(`/api/get_indicator/${symbol}/${timeframe}/ATR`);

// **9️⃣ cTrader**
export const getCTraderLoginURL = async (accountId) => {
  const response = await axios.get(`${API_BASE_URL}/ctrader/redirect`, {
    params: { account_id: accountId },
    withCredentials: true,
  });
  return response.data.redirect_url;
};

// **1️⃣0️⃣ Risk Management**
export const calculateLotSize = (params) => api.post("/risk/calculate_lot_size/", params);
export const fetchRiskSettings = (accountId, options = {}) => api.get(`/risk/settings/${accountId}/`, options);

// **1️⃣1️⃣ Trade History & Journals**
export const getTradeHistory = (trade_uuid) => api.get(`/history/trades/${trade_uuid}/`);
export const getAccountTrades = (account_uuid, params) => {
  const config = params ? { params } : undefined;
  return api.get(`/history/accounts/${account_uuid}/trades/`, config);
};
export const getAccountPerformanceStats = (accountId, startDate, endDate, options = {}) => {
  const params = new URLSearchParams();
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }
  if (options.source) {
    params.append('source', options.source);
  }
  if (options.instrument) {
    params.append('instrument', options.instrument);
  }
  return api.get(`/history/accounts/${accountId}/performance-stats/`, { params });
};
export const getEquityCurve = (accountId, startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }
  return api.get(`/equity-curve/${accountId}/`, { params });
};
export const getJournalsForTrade = (tradeId) => api.get(`/trade_journal/journals/?trade=${tradeId}`);

// **1️⃣2️⃣ Chart Snapshot Configuration**
export const getChartSnapshotConfigs = () => api.get("/chart-snapshots/configs/");
export const requestChartSnapshot = (configId, snapshotData) => api.post(`/chart-snapshots/configs/${configId}/execute/`, snapshotData);
export const createChartSnapshotConfig = (configData) => api.post("/chart-snapshots/configs/", configData);
export const getChartSnapshotConfigDetails = (configId) => api.get(`/chart-snapshots/configs/${configId}/`);
export const updateChartSnapshotConfig = (configId, configData) => api.put(`/chart-snapshots/configs/${configId}/`, configData);
export const deleteChartSnapshotConfig = (configId) => api.delete(`/chart-snapshots/configs/${configId}/`);

// **1️⃣3️⃣ Historical Data Analysis**
export const getAnalysisTypes = () => api.get("/analysis/types/");
export const getAnalysisTypeDetails = (analysisType) => api.get(`/analysis/types/${analysisType}/`);
export const addInstrument = (instrumentData) => api.post("/analysis/instruments/", instrumentData);
export const listInstruments = () => api.get("/analysis/instruments/");
export const submitAnalysisJob = (jobData) => api.post("/analysis/submit/", jobData);
export const listAnalysisJobs = () => api.get("/analysis/jobs/");
export const getAnalysisJobStatus = (jobId) => api.get(`/analysis/jobs/${jobId}/status/`);
export const deleteAnalysisJob = (jobId) => api.delete(`/analysis/jobs/${jobId}/status/`);
export const getAnalysisJobResults = (jobId) => api.get(`/analysis/jobs/${jobId}/results/`);

// **1️⃣4️⃣ Indicators & Chart Profiles**
export const fetchAvailableIndicators = () => api.get("/indicators/available/");
export const listChartProfiles = () => api.get("/charts/profiles/");
export const createChartProfile = (name, symbol, timeframe, indicators) => api.post("/charts/profiles/", { name, symbol, timeframe, indicators });
export const retrieveChartProfile = (profileId) => api.get(`/charts/profiles/${profileId}/`);
export const updateChartProfile = (profileId, profileData) => api.put(`/charts/profiles/${profileId}/`, profileData);
export const deleteChartProfile = (profileId) => api.delete(`/charts/profiles/${profileId}/`);

// 14.1 Indicator Presets
// List presets; optionally filter by indicator registry key (?indicator=trend_strength)
export const listIndicatorPresets = (params) => api.get(`/indicators/presets/`, params ? { params } : undefined);
// Create a preset: { indicator, name, description?, params }
export const createIndicatorPreset = (payload) => api.post(`/indicators/presets/`, payload, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
// Retrieve a preset by id
export const getIndicatorPreset = (presetId) => api.get(`/indicators/presets/${encodeURIComponent(presetId)}/`);
// Update (partial) a preset by id: any of { name, description, params }
export const updateIndicatorPreset = (presetId, payload) => api.patch(`/indicators/presets/${encodeURIComponent(presetId)}/`, payload, { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } });
// Delete a preset by id
export const deleteIndicatorPreset = (presetId) => api.delete(`/indicators/presets/${encodeURIComponent(presetId)}/`);

// **1️⃣5️⃣ User & Chat**
export const getUserApiToken = () => api.get("/user/create-token/");
export const rotateUserApiToken = () => api.post("/user/create-token/", {});
export const revokeUserApiToken = () => api.delete("/user/create-token/");
export const CreateUserToken = getUserApiToken;
// Accept optional params for pagination, e.g., { limit, offset } or { page, page_size }
export const fetchChatSessions = (params) => api.get("/ai/chat-sessions/", { params });

// Archive / Unarchive chat sessions
export const archiveChatSession = (id) => api.post(`/ai/chat-sessions/${encodeURIComponent(id)}/archive/`);
export const unarchiveChatSession = (id) => api.post(`/ai/chat-sessions/${encodeURIComponent(id)}/unarchive/`);

// Chat Session Schedules CRUD (flat endpoints + session_id in payload/query)
export const listSessionSchedules = (sessionId) => {
  const cfg = {};
  if (sessionId) cfg.params = { session_id: sessionId };
  return api.get(`/ai/session-schedules/`, cfg);
};
export const createSessionSchedule = (data) => api.post(`/ai/session-schedules/`, data);
export const updateSessionSchedule = (scheduleId, data) => api.put(`/ai/session-schedules/${encodeURIComponent(scheduleId)}/`, data);
export const deleteSessionSchedule = (scheduleId) => api.delete(`/ai/session-schedules/${encodeURIComponent(scheduleId)}/`);
// Move a session schedule to a brand new chat session
export const moveSessionScheduleToNewSession = (scheduleId) => api.post(`/ai/session-schedules/${encodeURIComponent(scheduleId)}/move-to-new-session/`);

// --- Notifications API ---
// Some endpoints require reading response headers (e.g., X-Notifications-Unread).
// The api() interceptor returns data only, so for those we use axios directly.

/**
 * List notifications with optional filters (e.g., { unread: 1, category: 'SYSTEM' }).
 * Returns both data and headers so the caller can access X-Notifications-Unread.
 * @param {Object} [params]
 * @returns {Promise<{ data: Array, headers: Record<string,string>, status: number }>}
 */
export const listNotifications = async (params) => {
  const token = localStorage.getItem('token');
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter((entry) => {
      const v = entry[1];
      return v !== undefined && v !== null && v !== '';
    })
  );
  if (cleanParams.unread) cleanParams.unread = 1;
  const resp = await axios.get(`${API_BASE_URL}/api/notifications/`, {
    params: cleanParams,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    withCredentials: false,
  });
  return { data: resp.data, headers: resp.headers, status: resp.status };
};

/**
 * Fetch notifications by full cursor URL (for pagination).
 * @param {string} url - The full next/previous cursor URL from the API.
 * @returns {Promise<{ data: object, headers: Record<string,string>, status: number }>}
 */
export const listNotificationsByUrl = async (url) => {
  const token = localStorage.getItem('token');
  const resp = await axios.get(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    withCredentials: false,
  });
  return { data: resp.data, headers: resp.headers, status: resp.status };
};

/**
 * Get unread count (optionally filtered by category).
 * @param {Object} [params]
 * @returns {Promise<number>}
 */
export const getNotificationsUnreadCount = async (params, options = {}) => {
  const resp = await api.get(`/notifications/unread-count/`, { params, ...options });
  // Backend may return { count: number } or a raw number; normalize to number
  if (typeof resp === 'number') return resp;
  if (resp && typeof resp.count === 'number') return resp.count;
  return 0;
};

/**
 * Mark specific notifications as read.
 * @param {Array<string|number>} ids
 */
export const markNotificationsRead = (ids) => api.post(`/notifications/mark-read/`, { ids: Array.isArray(ids) ? ids : [ids] });

/**
 * Mark all notifications as read.
 */
export const markAllNotificationsRead = () => api.post(`/notifications/mark-all-read/`);

/**
 * Get notification preferences.
 */
export const getNotificationPreferences = (options = {}) => api.get(`/notifications/preferences/`, options);

/**
 * Upsert a notification preference for a channel/category.
 * Example body: { channel: 'WEB', category: 'SYSTEM', enabled: true, min_severity: 'INFO' }
 */
export const upsertNotificationPreference = (pref) => api.post(`/notifications/preferences/upsert/`, pref);

// **1️⃣6️⃣ User Studio Layouts**
export const listStudioLayouts = () => api.get("/user/user-studio-layouts/");
export const createStudioLayout = (layoutData) => api.post("/user/user-studio-layouts/", layoutData);
export const updateStudioLayout = (layoutId, layoutData) => api.put(`/user/user-studio-layouts/${layoutId}/`, layoutData);
export const deleteStudioLayout = (layoutId) => api.delete(`/user/user-studio-layouts/${layoutId}/`);

// Dashboard workspace presets
export const getDashboardProfile = () => api.get('/user/dashboard-profile/');
export const updateDashboardProfile = (profileData) => api.put('/user/dashboard-profile/', profileData);
export const listDashboardPresets = () => api.get('/user/dashboard-presets/');
export const createDashboardPreset = (presetData) => api.post('/user/dashboard-presets/', presetData);
export const updateDashboardPreset = (presetId, presetData) => api.put(`/user/dashboard-presets/${presetId}/`, presetData);
export const deleteDashboardPreset = (presetId) => api.delete(`/user/dashboard-presets/${presetId}/`);

// **1️⃣7️⃣ Fundamentals**
export const listEconomicCalendar = (params /* URLSearchParams or object */) => {
  const finalParams = params instanceof URLSearchParams ? params : new URLSearchParams(params || {});
  return api.get(`/fundamental/economic-calendar-list/`, {
    params: finalParams,
  });
};

export const fetchEconomicEventHistory = (eventName, currency, fromDate, toDate) => {
  const params = new URLSearchParams();
  if (eventName) params.append("event", eventName);
  if (currency) params.append("currency", currency);
  if (fromDate) params.append("date_from", fromDate);
  if (toDate) params.append("date_to", toDate);
  return api.get(`/fundamental/economic-calendar-list/`, { params });
};

// News API
export const listNews = (params = {}) => {
  const finalParams = params instanceof URLSearchParams ? params : new URLSearchParams();
  if (params.source) finalParams.append("source", params.source);
  if (params.date_from) finalParams.append("date_from", params.date_from);
  if (params.date_to) finalParams.append("date_to", params.date_to);
  if (params.tags) finalParams.append("tags", params.tags);
  if (params.search) finalParams.append("search", params.search);
  if (params.page) finalParams.append("page", params.page);
  if (params.page_size) finalParams.append("page_size", params.page_size);
  return api.get(`/fundamental/news-list/`, { params: finalParams });
};

export const listNewsTags = () => {
  return api.get(`/fundamental/news-tags/`);
};

// COT (Commitment of Traders) Reports
export const listCOTReports = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.symbol) finalParams.append("symbol", params.symbol);
  if (params.date_from) finalParams.append("date_from", params.date_from);
  if (params.date_to) finalParams.append("date_to", params.date_to);
  if (params.page) finalParams.append("page", params.page);
  if (params.page_size) finalParams.append("page_size", params.page_size);
  return api.get(`/fundamental/cot/list/`, { params: finalParams });
};

export const fetchCOTData = (symbol, startDate, endDate) => {
  const params = new URLSearchParams();
  if (symbol) params.append("symbol", symbol);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  return api.post(`/fundamental/cot/fetch/`, null, { params });
};

// Insider Trading (SEC Form 4)
export const listInsiderTrades = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.ticker) finalParams.append("ticker", params.ticker);
  if (params.insider_name) finalParams.append("insider_name", params.insider_name);
  if (params.transaction_type) finalParams.append("transaction_type", params.transaction_type);
  if (params.owner_type) finalParams.append("owner_type", params.owner_type);
  if (params.date_from) finalParams.append("date_from", params.date_from);
  if (params.date_to) finalParams.append("date_to", params.date_to);
  if (params.min_value) finalParams.append("min_value", params.min_value);
  if (params.page) finalParams.append("page", params.page);
  if (params.page_size) finalParams.append("page_size", params.page_size);
  return api.get(`/fundamental/insider-trades/list/`, { params: finalParams });
};

// Economic Indicators (FRED)
export const listEconomicIndicators = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.country) finalParams.append("country", params.country);
  if (params.currency) finalParams.append("currency", params.currency);
  if (params.indicator) finalParams.append("indicator", params.indicator);
  if (params.date_from) finalParams.append("date_from", params.date_from);
  if (params.date_to) finalParams.append("date_to", params.date_to);
  return api.get(`/fundamental/indicators/`, { params: finalParams });
};

export const getEconomicIndicatorsMeta = () => {
  return api.get(`/fundamental/indicators/meta/`);
};

export const getOptionsChain = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.symbol) finalParams.append("symbol", params.symbol);
  if (params.expiration_date) finalParams.append("expiration_date", params.expiration_date);
  if (params.option_type) finalParams.append("option_type", params.option_type);
  if (params.near_the_money) finalParams.append("near_the_money", params.near_the_money);
  return api.get(`/fundamental/options/chain/`, { params: finalParams });
};

// Trade Journal Entries
export const listJournalEntries = (params = {}, options = {}) => {
  const finalParams = new URLSearchParams();
  if (params.account_id) finalParams.append("account_id", params.account_id);
  if (params.trade_id) finalParams.append("trade", params.trade_id);
  if (params.limit) finalParams.append("limit", params.limit);
  return api.get(`/trade_journal/journals/`, { params: finalParams, ...options });
};

export const createJournalEntry = (data) => api.post(`/trade_journal/journals/`, data);

// **1️⃣8️⃣ Prompts (AI Instructions)**
export const listPrompts = (params) => api.get(`ai/prompts/`, { params });
export const createPrompt = (data) => api.post(`ai/prompts/`, data);
export const updatePrompt = (id, data) => api.put(`ai/prompts/${encodeURIComponent(id)}/`, data);
export const deletePrompt = (id) => api.delete(`ai/prompts/${encodeURIComponent(id)}/`);

// **1️⃣9️⃣ User Profile**
// Fetch the authenticated user's profile
export const getUserProfile = (options = {}) => api.get(`/accounts/profile/`, options);
// Update the authenticated user's profile (partial by default)
export const updateUserProfile = (data, { method = 'patch' } = {}) => {
  const headers = { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
  if (method.toLowerCase() === 'put') {
    return api.put(`/accounts/profile/`, data, headers).then((response) => {
      emitProfileUpdated();
      return response;
    });
  }
  return api.patch(`/accounts/profile/`, data, headers).then((response) => {
    emitProfileUpdated();
    return response;
  });
};

// \u2b50\ufe0f Portfolio (All Accounts) Snapshot
// Fetches a combined snapshot for all or a subset of accounts.
// Optional: pass an array of account UUIDs to filter. Returns
// { accounts: [...], positions: [...], pending_orders: [...] }
export const fetchPortfolioSnapshot = (accountIds) => {
  const params = {};
  if (Array.isArray(accountIds) && accountIds.length) {
    params.accounts = accountIds.join(',');
  }
  return api.get(`/accounts/portfolio/snapshot/`, { params });
};

// **20️⃣ Telegram Linking**
// GET /api/comms/telegram/status -> { linked: boolean, chat_id?: string, is_active?: boolean }
export const getTelegramStatus = () => api.get(`/comms/telegram/status`);
// POST /api/comms/telegram/start_link -> { code, expires_at, bot_username?, deep_link?, instructions }
export const startTelegramLink = () => api.post(`/comms/telegram/start_link`);
// POST /api/comms/telegram/unlink -> { detail: "unlinked" }
export const unlinkTelegram = () => api.post(`/comms/telegram/unlink`);

// **21️⃣ Main Assistant (AI Chatbot)**
// Base path for agent-system API
const AGENT_SYSTEM_BASE = '/ai/expert/api/agent-system';

// List all user's agents (for agent selector)
export const listUserAgents = (params = {}) => {
  return api.get(`${AGENT_SYSTEM_BASE}/agents/`, { params });
};

// Get a specific agent by ID
export const getAgent = (agentId) => {
  return api.get(`${AGENT_SYSTEM_BASE}/agents/${encodeURIComponent(agentId)}/`);
};

// Get the user's main assistant agent (auto-creates if doesn't exist)
export const getMainAssistant = () => api.get(`${AGENT_SYSTEM_BASE}/main-assistant/`);

// Send a message to the main assistant (non-streaming, returns execution_id)
export const sendMainAssistantMessage = (message, sessionId = null, context = {}) => {
  return api.post(`${AGENT_SYSTEM_BASE}/main-assistant/`, {
    message,
    session_id: sessionId,
    context,
    stream: false,
  });
};

// List main assistant chat sessions
export const listMainAssistantSessions = (params = {}) => {
  return api.get(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/`, { params });
};

// List sessions for a specific agent (or all if no agentId)
export const listAgentSessions = (agentId = null, params = {}) => {
  const queryParams = { ...params };
  if (agentId) {
    queryParams.agent_id = agentId;
  }
  return api.get(`${AGENT_SYSTEM_BASE}/sessions/`, { params: queryParams });
};

// Fork a conversation session into a new branch
export const forkAgentSession = (sessionId, payload = {}) => {
  return api.post(`${AGENT_SYSTEM_BASE}/sessions/${encodeURIComponent(sessionId)}/fork/`, payload);
};

// Create a new main assistant session
export const createMainAssistantSession = (title = '') => {
  return api.post(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/`, { title });
};

// Get main assistant session details with messages
export const getMainAssistantSession = (sessionId, includeMessages = true, messageLimit = 50) => {
  return api.get(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/${encodeURIComponent(sessionId)}/`, {
    params: {
      include_messages: includeMessages,
      message_limit: messageLimit,
    },
  });
};

// Archive a main assistant session
export const archiveMainAssistantSession = (sessionId) => {
  return api.post(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/${encodeURIComponent(sessionId)}/archive/`);
};

// Unarchive a main assistant session  
export const unarchiveMainAssistantSession = (sessionId) => {
  return api.post(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/${encodeURIComponent(sessionId)}/unarchive/`);
};

// Delete (archive) a main assistant session
export const deleteMainAssistantSession = (sessionId) => {
  return api.delete(`${AGENT_SYSTEM_BASE}/main-assistant/sessions/${encodeURIComponent(sessionId)}/`);
};

// Get agent execution status
export const getAgentExecution = (executionId) => {
  return api.get(`${AGENT_SYSTEM_BASE}/executions/${encodeURIComponent(executionId)}/`);
};

// ── Agent Export / Import / Clone ───────────────────────────────
// Export an agent (and, by default, its sub-agent graph) as a JSON payload.
export const exportAgent = (agentId, { maskApiKeys = true, includeSubAgents = true } = {}) => {
  return api.get(`${AGENT_SYSTEM_BASE}/agents/${encodeURIComponent(agentId)}/export/`, {
    params: {
      mask_api_keys: maskApiKeys,
      include_sub_agents: includeSubAgents,
    },
  });
};

// Import an agent export payload. Accepts either a raw payload or an options
// object `{ data, apiKeyMapping, nameSuffix, forceNew, replaceExisting }`.
export const importAgent = (payloadOrOptions) => {
  const opts =
    payloadOrOptions && payloadOrOptions.export_type === 'agent'
      ? { data: payloadOrOptions }
      : payloadOrOptions || {};
  const body = {
    data: opts.data,
    api_key_mapping: opts.apiKeyMapping || undefined,
    name_suffix: opts.nameSuffix || undefined,
    force_new: opts.forceNew,
    replace_existing: opts.replaceExisting,
  };
  return api.post(`${AGENT_SYSTEM_BASE}/agents/import/`, body);
};

// Clone an agent (including sub-agents) as a new agent for the current user.
export const cloneAgent = (agentId, { name } = {}) => {
  const body = name ? { name } : {};
  return api.post(`${AGENT_SYSTEM_BASE}/agents/${encodeURIComponent(agentId)}/clone/`, body);
};

// ── Knowledge Graph API ─────────────────────────────────────────
export const getKnowledgeGraphStats = () =>
  api.get("/fundamental/knowledge-graph/stats/");

export const getKnowledgeGraphEvents = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.event_type) finalParams.append("event_type", params.event_type);
  if (params.currency) finalParams.append("currency", params.currency);
  if (params.symbol) finalParams.append("symbol", params.symbol);
  if (params.date_from) finalParams.append("date_from", params.date_from);
  if (params.date_to) finalParams.append("date_to", params.date_to);
  if (params.search) finalParams.append("search", params.search);
  if (params.min_impact) finalParams.append("min_impact", params.min_impact);
  if (params.page) finalParams.append("page", params.page);
  if (params.page_size) finalParams.append("page_size", params.page_size);
  return api.get("/fundamental/knowledge-graph/events/", { params: finalParams });
};

export const getKnowledgeGraphSymbols = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.search) finalParams.append("search", params.search);
  if (params.page) finalParams.append("page", params.page);
  if (params.page_size) finalParams.append("page_size", params.page_size);
  if (params.days_back != null) finalParams.append("days_back", params.days_back);
  if (params.currency) finalParams.append("currency", params.currency);
  if (params.event_type) finalParams.append("event_type", params.event_type);
  return api.get("/fundamental/knowledge-graph/symbols/", { params: finalParams });
};

export const getKnowledgeGraphGraphData = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.currency) finalParams.append("currency", params.currency);
  if (params.symbol) finalParams.append("symbol", params.symbol);
  if (params.event_type) finalParams.append("event_type", params.event_type);
  if (params.days_back != null) finalParams.append("days_back", params.days_back);
  if (params.limit != null) finalParams.append("limit", params.limit);
  if (params.granularity) finalParams.append("granularity", params.granularity);
  if (params.move_type) finalParams.append("move_type", params.move_type);
  return api.get("/fundamental/knowledge-graph/graph-data/", { params: finalParams });
};

export const getCurrencyNarratives = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.timeframe) finalParams.append("timeframe", params.timeframe);
  if (params.currency) finalParams.append("currency", params.currency);
  return api.get("/fundamental/knowledge-graph/narratives/", { params: finalParams });
};

export const getCausalChain = (eventId, params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.direction) finalParams.append("direction", params.direction);
  if (params.max_depth) finalParams.append("max_depth", params.max_depth);
  return api.get(`/fundamental/knowledge-graph/chain/${eventId}/`, { params: finalParams });
};

export const getKnowledgeGraphPatterns = (params = {}) => {
  const finalParams = new URLSearchParams();
  if (params.days_back) finalParams.append("days_back", params.days_back);
  if (params.min_occurrences) finalParams.append("min_occurrences", params.min_occurrences);
  return api.get("/fundamental/knowledge-graph/patterns/", { params: finalParams });
};

export default api;
